<script setup lang="ts">
import { SingleProgrammeTypeQuery } from '~/queries/single/programme-type'

definePageMeta({ path: '/exhibitions-events/:type()' })

const route = useRoute()
const { locale } = useI18n()

const typeSlug = route.params.type as string

const runtimeConfig = useRuntimeConfig()
const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

const { data: typeData, error: typeError } = await useAsyncData(
  `programme-type-${typeSlug}-${locale.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: SingleProgrammeTypeQuery,
      variables: {
        slug: typeSlug,
        locale: locale.value,
        isPreview: isPreview.value,
        // with `draft: true` payload returns each doc's latest version, whatever its
        // status -- so preview must accept both, or a doc with no pending draft
        // (latest version published) matches nothing and the page renders empty
        statuses: isPreview.value ? ['draft', 'published'] : ['published'],
      },
      isPreview: isPreview.value,
    },
  })
)

if (typeError.value) {
  throw createError({ statusCode: 500, statusMessage: 'Error fetching programme type' })
}

const programmeType = computed(() => (typeData.value as any)?.docs?.[0])

if (!programmeType.value && !isPreview.value) {
  throw createError({ statusCode: 404, statusMessage: 'Programme type not found' })
}

usePageSeo({
  title: () => programmeType.value?.title,
})
</script>

<template>
  <page-layout>
    <template #header>
      <page-header
        :title="programmeType?.title || ''"
        :parent="{
          name: 'programme',
        }"
      />
    </template>

    <template #default>
      <pages-programme-type
        v-if="programmeType"
        :programme-type="programmeType"
      />
    </template>
  </page-layout>
</template>
